import { inject, injectable } from '@patsnap/tahiti-core/shared/inversify';
import { ParsedFiguresApi } from '@/domains/specification/apis/parsed-figures-api';
import { CurrentTaskService } from '@/services/current-task/current-task-service';
import type { ImageSaveService, SaveImagesOptions, SaveImagesResult } from '../image-save-service';

/**
 * 图片保存服务实现
 * 基于 ParsedFiguresApi 将选中的图片保存到服务器
 */
@injectable()
export class ImageSaveServiceImpl implements ImageSaveService {
    constructor(
        @inject(ParsedFiguresApi)
        private readonly _parsedFiguresApi: ParsedFiguresApi,
        @inject(CurrentTaskService)
        private readonly _currentTaskService: CurrentTaskService,
    ) {}

    async saveSelectedImages(options: SaveImagesOptions): Promise<SaveImagesResult> {
        try {
            // 获取任务ID
            const taskId = options.taskId || this._currentTaskService.taskId;

            if (!taskId) {
                throw new Error('No task ID available for saving images');
            }

            // 转换 ImageItem 为 ParsedImage 格式
            const parsedImages = options.images.map((image, index) => ({
                imageIndex: index,
                imageName: this._extractFileNameFromUrl(image.url),
                imageFormat: this._extractFormatFromUrl(image.url),
                width: 0, // 默认值，实际应从图片元数据获取
                height: 0, // 默认值，实际应从图片元数据获取
                imageSize: 0, // 默认值，实际应从图片元数据获取
                imageUrl: image.url,
                s3Key: image.s3Key,
                uniqueId: image.s3Key, // 使用 s3Key 作为唯一标识
            }));

            // 调用 API 保存图片
            const response = await this._parsedFiguresApi.saveParsedFigures({
                taskId,
                image_s3_keys: parsedImages.map(image => image.s3Key),
            });

            // 处理响应
            if (response) {
                // 如果保存成功，返回所有图片信息，并包含从response获取的uniqueId
                return parsedImages.map(image => ({
                    url: image.imageUrl,
                    s3Key: image.s3Key,
                    uniqueId: response.s3_key_to_figure_id_map[image.s3Key] || image.s3Key,
                }));
            }

            // 如果保存失败，返回空数组
            return [];
        } catch (error) {
            console.error('Failed to save selected images:', error);

            // 保存失败时返回空数组
            return [];
        }
    }

    /**
     * 从URL中提取文件名
     */
    private _extractFileNameFromUrl(url: string): string {
        try {
            const urlPath = new URL(url).pathname;
            return urlPath.split('/').pop() || 'unknown';
        } catch {
            return 'unknown';
        }
    }

    /**
     * 从URL中提取文件格式
     */
    private _extractFormatFromUrl(url: string): string {
        try {
            const fileName = this._extractFileNameFromUrl(url);
            const extension = fileName.split('.').pop()?.toLowerCase();
            return extension || 'jpg';
        } catch {
            return 'jpg';
        }
    }
}
